import { Link } from 'react-router';
import { Trophy } from 'lucide-react';
import type { ReactNode } from 'react';

const FOOTER_LINKS = [
  { to: '/polityka-prywatnosci', label: 'Polityka prywatności' },
  { to: '/regulamin', label: 'Regulamin' },
  { to: '/usuniecie-konta', label: 'Usunięcie konta' },
  { to: '/wsparcie', label: 'Wsparcie' },
] as const;

export function LegalPageShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <main className="min-h-screen bg-white text-neutral-900">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(1400px_800px_at_50%_0%,rgba(0,102,255,0.06),transparent_70%)]" />
      </div>

      <header className="sticky top-0 z-50 border-b border-neutral-200/60 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative h-11 w-11 rounded-2xl bg-gradient-to-br from-[#0066FF] to-[#00C7B1] text-white grid place-items-center font-bold text-base shadow-lg">
              <Trophy className="w-6 h-6" />
            </div>
            <span className="text-xl font-extrabold tracking-tight bg-gradient-to-r from-[#0066FF] to-[#00C7B1] bg-clip-text text-transparent">
              Turnivo
            </span>
          </Link>
          <Link to="/" className="text-sm font-medium text-neutral-600 hover:text-neutral-900 transition">
            ← Strona główna
          </Link>
        </div>
      </header>

      <article className="mx-auto max-w-4xl px-6 py-12 md:py-16">
        <h1 className="text-4xl font-extrabold tracking-tight text-neutral-900 mb-2">{title}</h1>
        {subtitle ? <p className="text-neutral-500 text-sm mb-10">{subtitle}</p> : <div className="mb-10" />}
        <div className="prose prose-neutral max-w-none space-y-8 text-neutral-700 leading-relaxed">
          {children}
        </div>
      </article>

      <footer className="border-t border-neutral-200 bg-white mt-16">
        <div className="mx-auto flex max-w-4xl flex-col gap-4 px-6 py-8 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-[#0066FF] to-[#00C7B1] text-white grid place-items-center font-bold text-sm shadow-lg">
              TU
            </div>
            <div>
              <div className="text-base font-extrabold">Turnivo</div>
              <div className="text-xs text-neutral-500">Organizacja turniejów siatkarskich</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-neutral-500">
            {FOOTER_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="hover:text-neutral-900 transition">
                {l.label}
              </Link>
            ))}
            <span>© {new Date().getFullYear()} Turnivo</span>
          </div>
        </div>
      </footer>
    </main>
  );
}

export function LegalSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">{title}</h2>
      {children}
    </section>
  );
}
